import { useCallback, useEffect, useRef, useState } from 'react';
import { describeDbError } from '../lib/errors';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-runs the loader. Safe to call from an event handler. */
  reload: () => void;
  /** Replaces the data locally, for optimistic edits. */
  setData: (next: T) => void;
}

/**
 * Runs an async loader on mount and whenever `key` changes.
 *
 * `key` rather than a dependency array: the loader is almost always a closure
 * that changes identity on every render, and passing it as a dependency is how
 * you get an infinite request loop. The caller states what the data actually
 * depends on — usually a trip id.
 *
 * A stale response is dropped rather than applied. Without that, navigating
 * from one trip to another quickly can leave the first trip's expenses
 * rendered under the second trip's name.
 */
export function useAsync<T>(loader: () => Promise<T>, key: string): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Held in a ref so changing the closure does not re-trigger the effect.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    let cancelled = false;

    setLoading(true);
    setError(null);

    loaderRef
      .current()
      .then((result) => {
        if (cancelled || request !== requestRef.current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (cancelled || request !== requestRef.current) return;
        setError(describeDbError(err));
      })
      .finally(() => {
        if (cancelled || request !== requestRef.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, setData };
}
